import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import axios from 'axios'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'

import {
  Valve,
  ValveKey,
  ValveSettingsField,
  ValveSettingsPatch,
} from '@data/device/model/device'
import { useValveSettingsUpdate } from '@data/device/device'
import { describeApiError } from '@common/utils/apiErrors'

// Shared by the Device Modal's Valves tab and the standalone Operate Valves page — both
// stage per-valve name/manufacturer/size edits locally and commit them in one PUT.
export const useValveSettingsDrafts = (dUuid: string) => {
  const { mutate: saveValveSettings, isPending: isSavingValves } = useValveSettingsUpdate()

  const [drafts, setDrafts] = useState<Partial<Record<ValveKey, ValveSettingsPatch>>>({})

  // Drafts are per-device — never carry one device's staged edits onto another.
  useEffect(() => {
    setDrafts({})
  }, [dUuid])

  // Edits are staged here until saved, so a draft holds only the fields that differ from
  // what the device already has — an empty draft is dropped rather than kept as a no-op.
  const handleSettingsChange = useCallback((valve: Valve, patch: ValveSettingsPatch) => {
    setDrafts((prev) => {
      const merged: ValveSettingsPatch = { ...prev[valve.key], ...patch }

      const changed = (Object.keys(merged) as ValveSettingsField[]).reduce<ValveSettingsPatch>(
        (acc, field) => {
          if ((merged[field] ?? null) !== (valve[field] ?? null)) {
            Object.assign(acc, { [field]: merged[field] })
          }
          return acc
        },
        {},
      )

      const next = { ...prev }
      if (Object.keys(changed).length) next[valve.key] = changed
      else delete next[valve.key]
      return next
    })
  }, [])

  const handleDiscardValves = useCallback(() => {
    setDrafts({})
  }, [])

  const handleSaveValves = useCallback(() => {
    const valveSettings = (Object.entries(drafts) as [ValveKey, ValveSettingsPatch][])
      .map(([valveId, patch]) => ({ valveId, ...patch }))

    if (!valveSettings.length) return

    // One request for every modified valve — the upsert leaves untouched valves and
    // omitted fields alone.
    saveValveSettings(
      {
        dUuid,
        valveSettings,
      },
      {
        onSuccess: () => {
          setDrafts({})
          notifications.show({
            title: 'Valves updated',
            message: `${valveSettings.length} valve${valveSettings.length === 1 ? '' : 's'} saved successfully.`,
            color: 'green',
            icon: <IconCheck size={16} />,
          })
        },
        onError: (error) => {
          notifications.show({
            title: 'Unable to save valve settings',
            message:
              axios.isAxiosError(error) && error.response?.status === 422
                ? 'Valve settings are invalid. Check each valve ID, manufacturer, and size.'
                : describeApiError(error, 'Failed to save valve changes.'),
            color: 'red',
          })
        },
      },
    )
  }, [dUuid, drafts, saveValveSettings])

  return {
    drafts,
    modifiedCount: Object.keys(drafts).length,
    isSavingValves,
    handleSettingsChange,
    handleDiscardValves,
    handleSaveValves,
  }
}
